import { Column, Table, flexRender } from '@tanstack/react-table';
import { Tooltip } from '@benzinga/core-ui';
import type { SortBy } from '../../libs/utils';
import { HeaderSortIcons, THeadStyled } from '../Table/TableHead';
import { CombinedColumnDefTS } from './index';

export interface TableHeadProps<T> {
  $alignLastRowsRight?: boolean;
  $hasStockTableStyling?: boolean;
  hiddenColumnsWhenGated?: Set<string>;
  isGated?: boolean;
  onSortChange: (newSortByField: string, column: Column<T, unknown>) => void;
  sortBy: SortBy;
  table: Table<T>;
}

export const TableHead = <T,>({
  $alignLastRowsRight,
  $hasStockTableStyling,
  hiddenColumnsWhenGated,
  isGated,
  onSortChange,
  sortBy,
  table,
}: TableHeadProps<T>) => {
  return (
    <THeadStyled
      $alignLastRowsRight={$alignLastRowsRight}
      $hasStockTableStyling={$hasStockTableStyling}
      className="benzinga-core-virtualized-table-thead whitespace-nowrap"
    >
      {table.getHeaderGroups().map(headerGroup => (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const columnDef = header.column.columnDef as CombinedColumnDefTS<T>;

            if (isGated && hiddenColumnsWhenGated?.has(columnDef.field)) return null;

            const defaultHeaderContent = flexRender(columnDef.header, header.getContext());
            const thContent = columnDef.headerRenderer ? (
              columnDef.headerRenderer()
            ) : sortBy && columnDef.field && columnDef.sortEnabled && onSortChange ? (
              <button
                className="inline-flex items-center gap-1 leading-none"
                onClick={() => onSortChange(columnDef.field, header.column)}
                type="button"
              >
                <span className="capitalize">{defaultHeaderContent}</span>
                {sortBy && <HeaderSortIcons field={columnDef.field} sortBy={sortBy} />}
              </button>
            ) : (
              defaultHeaderContent
            );

            return (
              <th
                aria-hidden={columnDef.ariaHidden}
                className={`benzinga-virtualized-table-th capitalize ${columnDef.headerClass || ''}`}
                colSpan={header.colSpan}
                key={header.id}
                style={{ width: header.getSize() }}
              >
                {header.isPlaceholder ? null : columnDef.headerTooltip ? (
                  <Tooltip content={columnDef.headerTooltip} width={160}>
                    {thContent}
                  </Tooltip>
                ) : (
                  thContent
                )}
              </th>
            );
          })}
        </tr>
      ))}
    </THeadStyled>
  );
};
